import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Compass, Heart, MessageSquare, Users, Calendar, User,
  LogOut, ChevronLeft, ChevronRight,
  Shield, CalendarDays
} from 'lucide-react';
import { useAuth } from '../App';
import { BEALogoMark } from './ui/bea-logo';

const AppLayout = ({ children }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const navItems = [
    { path: '/discover', icon: Compass, label: 'Discover' },
    { path: '/shortlist', icon: Heart, label: 'Shortlist' },
    { path: '/intros', icon: Users, label: 'Intros' },
    { path: '/messages', icon: MessageSquare, label: 'Messages' },
    { path: '/events', icon: CalendarDays, label: 'Events' },
    { path: '/meetings', icon: Calendar, label: 'Meetings' },
    { path: '/profile', icon: User, label: 'Profile' },
  ];

  const mobileNavItems = navItems.slice(0, 5);

  return (
    <div className="min-h-screen bg-[var(--bea-bg)] flex">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex flex-col fixed left-0 top-0 bottom-0 z-50 border-r border-[var(--bea-border)] bg-[var(--bea-bg)] transition-all duration-300 ${
          collapsed ? 'w-[72px]' : 'w-[240px]'
        }`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-5 border-b border-[var(--bea-border)]">
          <Link to="/discover" className="flex items-center gap-3 overflow-hidden" data-testid="sidebar-logo">
            <BEALogoMark size={28} />
            {!collapsed && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="font-bold text-[var(--bea-text)] text-lg whitespace-nowrap"
              >
                BEA Connect
              </motion.span>
            )}
          </Link>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {navItems.map(item => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all group ${
                  isActive
                    ? 'text-[var(--bea-text)]'
                    : 'text-[var(--bea-text-secondary)] hover:text-[var(--bea-text)] hover:bg-[var(--bea-overlay)]'
                }`}
                data-testid={`sidebar-${item.label.toLowerCase()}`}
              >
                {isActive && (
                  <motion.div
                    layoutId="sidebarActive"
                    className="absolute inset-0 rounded-xl bg-[#6B21A8]/10 border border-[#6B21A8]/20"
                    transition={{ type: "spring", bounce: 0.15, duration: 0.5 }}
                  />
                )}
                <Icon className={`w-5 h-5 relative z-10 flex-shrink-0 ${isActive ? 'text-[#6B21A8]' : ''}`} />
                {!collapsed && (
                  <span className="relative z-10 text-sm font-medium whitespace-nowrap">{item.label}</span>
                )}
              </Link>
            );
          })}

          {user?.is_admin && (
            <Link
              to="/admin"
              className={`relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                location.pathname === '/admin'
                  ? 'text-[var(--bea-text)] bg-[#F97316]/10 border border-[#F97316]/20'
                  : 'text-[var(--bea-text-secondary)] hover:text-[var(--bea-text)] hover:bg-[var(--bea-overlay)]'
              }`}
              data-testid="sidebar-admin"
            >
              <Shield className={`w-5 h-5 flex-shrink-0 ${location.pathname === '/admin' ? 'text-[#F97316]' : ''}`} />
              {!collapsed && <span className="text-sm font-medium">Admin</span>}
            </Link>
          )}
        </nav>

        {/* Bottom Section */}
        <div className="p-3 border-t border-[var(--bea-border)] space-y-1">
          <button
            onClick={() => { logout(); window.location.href = '/'; }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[var(--bea-text-secondary)] hover:text-red-500 hover:bg-red-500/[0.06] transition-all"
            data-testid="sidebar-logout"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span className="text-sm font-medium">Log Out</span>}
          </button>

          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center py-2 rounded-xl text-[var(--bea-text-muted)] hover:text-[var(--bea-text-secondary)] transition-all"
            data-testid="sidebar-collapse-toggle"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={`flex-1 min-h-screen transition-all duration-300 ${
          collapsed ? 'md:ml-[72px]' : 'md:ml-[240px]'
        } pb-20 md:pb-0`}
      >
        {children}
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-[var(--bea-bg)]/95 backdrop-blur-xl border-t border-[var(--bea-border)] safe-area-inset" data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around h-16 px-2">
          {mobileNavItems.map(item => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className="relative flex flex-col items-center justify-center w-16"
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              >
                {isActive && (
                  <motion.div
                    layoutId="activeMobileNav"
                    className="absolute -top-1 w-8 h-0.5 rounded-full bg-[#6B21A8]"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                  />
                )}
                <Icon className={`w-5 h-5 ${isActive ? 'text-[#6B21A8]' : 'text-[var(--bea-text-muted)]'}`} />
                <span className={`text-[10px] mt-1 ${isActive ? 'text-[#6B21A8] font-medium' : 'text-[var(--bea-text-muted)]'}`}>
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default AppLayout;
